import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  buildUpdateCourseRequest,
  fetchAuthoringCourses,
  fetchCourseContent,
  updateAuthoringCourse,
} from '../api/coursesApi';
import type { ContentDocument, ContentSlide } from '@course-renderer';
import type { AuthoringCoursesQueryParams } from '../types/AuthoringCourse';

export const authoringCoursesKeys = {
  all: ['authoringCourses'] as const,
  lists: () => [...authoringCoursesKeys.all, 'list'] as const,
  list: (params?: AuthoringCoursesQueryParams) => [...authoringCoursesKeys.lists(), params] as const,
  content: (courseId: string) => [...authoringCoursesKeys.all, 'content', courseId] as const,
};

/**
 * Hook to fetch the paginated and filtered list of authoring courses.
 */
export function useAuthoringCourses(params?: AuthoringCoursesQueryParams) {
  return useQuery({
    queryKey: authoringCoursesKeys.list(params),
    queryFn: () => fetchAuthoringCourses(params),
    staleTime: 30000,
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
    retry: 2,
  });
}

/**
 * Hook to fetch a course's slide content for the editor.
 *
 * `staleTime: Infinity` because the editor is the only writer: content changes when this
 * user saves, and the save path invalidates this key. Re-fetching on focus would discard
 * whatever the author has open. Same contract as `useTemplateContent`.
 */
export function useCourseContent(courseId: string) {
  return useQuery({
    queryKey: authoringCoursesKeys.content(courseId),
    queryFn: () => fetchCourseContent(courseId),
    enabled: Boolean(courseId),
    staleTime: Infinity,
    retry: 1,
  });
}

export interface UpdateCourseContentInput {
  courseId: string;
  loadedContent: ContentDocument;
  slides: ContentSlide[];
  expectedVersion?: number;
}

/**
 * Persists manual course edits via `PUT /courses/{id}`.
 * Invalidates content and list queries on success so the editor reloads the saved deck.
 */
export function useUpdateCourseContent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ courseId, loadedContent, slides, expectedVersion }: UpdateCourseContentInput) =>
      updateAuthoringCourse(courseId, buildUpdateCourseRequest(loadedContent, slides, expectedVersion)),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: authoringCoursesKeys.content(variables.courseId) }),
        queryClient.invalidateQueries({ queryKey: authoringCoursesKeys.lists() }),
      ]);
    },
  });
}
